'use client'
// Library Imports
import React, { SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'next/navigation';
import { Tab, Box, Button, Grid, Stack, Typography,} from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
// Icons
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import CloseIcon from '@mui/icons-material/Close';
// Utility Imports
import { isFeatureAvailable } from '@/utils/builder/featureCatalog';
import { DimensionalViewType, ProductPlacementType, SurfaceType, TRIGGER_ROOM_LAYOUT_THUMBNAILS_SAVE } from '@/utils/builder/builderConstants';
import { gtmClickTracking, gtmPageOnloadTracking } from '@/utils';

// Store Imports
import BuilderSelect from "@/store/builderStoreSelector";
import { Project, Texture, BuilderProduct } from '@/store/builderSlice';
import { actions as builderActions } from "@/store/builderSlice";
import { saveRoomLayout, saveRoomLayoutThumbnail } from '@/store/builder/thunks/roomModelsThunk';
import { saveProject } from '@/store/builder/thunks/projectSaveThunk';

// Component Imports
import TabPanel from './TabPanel';
import ClientComponent from './client_component';
import NotificationPopup from './NotificationPopup';
import { tabsMap } from './tabsMap';
import GetTrayComponentsForThreeDBuilder from './GetTrayComponentsForThreeDBuilder'
// scan images imports 

import { imageMap } from './ScannedImagesMap';

import InfoSection from './InfoSection';
import DesignDetail from './DesignDetail';
import MoreItems from './MoreItems';
import ProductDetails from './ProductDetails';

function getTabId(index: number) {
  return {
    id: `vertical-tab-${index}`,
  };
}

function ControlPanel({ showEditPanel = false }) {
  const dispatch = useDispatch();
  const [isPopupOpen, setPopupOpen] = useState(false);
  const [value, setValue] = useState<number | undefined>(0);
  const switchedTo2DModeRef = useRef(false);
  const [panelPosition, setPanelPosition] = useState({ x: 20, y: 80 });
  const currentRoomId: string = BuilderSelect.activeSelection().roomId;
  const isCustomRoomFlow: boolean = BuilderSelect.ui().isCustomRoomFlow;
  const [isInfoSectionVisible, setIsInfoSectionVisible] = useState<boolean>(false);
  const [openDesignPanel, setOpenDesignPanel] = useState<boolean>(true);
  const [openThreeDFurniturePanel, setOpenThreeDFurniturePanel] = useState<boolean>(true);
  const layoutBuilderMode: string = BuilderSelect.ui().layoutBuilderMode;
  const searchParams = useSearchParams();
  const [enableSaveButton, setEnableSaveButton] = useState(false);
  const [saveButtonTitle, setSaveButtonTitle] = useState("Save");
  const [isIOSWebKit, setIsIOSWebKit] = useState(false);

  const project: Project = BuilderSelect.project();
  const urlParams: { [key: string]: string } = BuilderSelect.ui().urlParams;
  const currentTab: string = BuilderSelect.ui().currentTab;
  const currentWallId: string = BuilderSelect.twoDRoomLayout().activeWallId;
  const captureThumbnailStatus = BuilderSelect.twoDRoomLayout().captureThumbnailStatus;
  const twoDLayoutSaveStatus = BuilderSelect.twoDRoomLayout().saveStatus;
  const thumbnailImage = BuilderSelect.twoDRoomLayout().thumbnailImage;
  let projectIdFromSave = "";
  const [expandLeftPanel, setExpandLeftPanel] = useState("");

  const handleExpandLeftPanelFromChild = (value:string) => {
    setExpandLeftPanel(value);
  };


  const getWindowDimensions = () => {
    const { innerWidth: width, innerHeight: height } = window;
    return {
      width,
      height
    };
  };
  const [windowDimensions, setWindowDimensions] = useState(
    getWindowDimensions()
  );



  const useWindowDimensions = () => {
   
    useEffect(() => {
      function handleResize() {
        setWindowDimensions(getWindowDimensions());
      }
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }, []);
    return windowDimensions;
  };

  const { width } = useWindowDimensions();

  useEffect(() => {
    if (width < 768) {
      setValue(undefined);
    }
  }, []);


  const isInteriorDesigner = urlParams.interiorDesigner === 'true'
  const currentRole=isInteriorDesigner ? 'interiorDesigner' : '';
  const designId = urlParams.designId;
  const productId = urlParams.productId;
  const isShowcaseFlow = urlParams.showcase === 'true' || isInteriorDesigner;
  const isDesignShowcase = urlParams.designShowcase === 'true';
  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false);


  useEffect(() => {
    if (typeof window !== 'undefined' && (window as any).webkit) {
      setIsIOSWebKit(true);
    }
  }, []);

  useEffect(() => {
    if (project.projectId) {
      let updatedSearchParams = new URLSearchParams(searchParams.toString());
      updatedSearchParams.set(isInteriorDesigner ? 'layoutId' : 'projectId', project.projectId);
      window.history.replaceState(null, '', '/builder?' + updatedSearchParams.toString());
    }
  }, [project.projectId, searchParams]);

  const projectProducts: BuilderProduct[] = project.products;
  useEffect(() => {
    dispatch(builderActions.addProductData({ products: projectProducts }))
  }, [projectProducts])

  const applyColorTo2DWall = useCallback((color: string) => {
    dispatch(builderActions.updateWallColor({
      color
    }));
    dispatch(builderActions.updatCurrentTab({
      currentTab: ''
    }));
    setValue(undefined);
  }, []);

  const updateWallColor = useCallback((color: string) => {
    dispatch(builderActions.updateWallColor({
      color,
      wallId: currentWallId
    }));
  }, [currentWallId]);


  const applyFloorTexturein2DView = useCallback((texture: Texture) => {
    dispatch(builderActions.updateFloorTexture({
      floorTextureId: texture.id
    }));
  }, []);

  const applyFloorTexturein3DView = useCallback((texture: Texture) => {
    dispatch(builderActions.addProductToProject({
      productId: texture.id,
      surfaceType: SurfaceType.FLOOR,
      surfaceId: SurfaceType.FLOOR,
      type: ProductPlacementType.Texture,
      undoable: true,
    }));
  }, []);

  const updateBuilderLayoutMode = () => {
    //debugger
    setIsEditPopupOpen(true);
    setEnableSaveButton(true);
    const targetMode = layoutBuilderMode === DimensionalViewType.TWO_D ? DimensionalViewType.THREE_D : DimensionalViewType.TWO_D;
    if (!switchedTo2DModeRef.current && targetMode === DimensionalViewType.TWO_D)
      dispatch(builderActions.updateTwoDUi({
        showLayoutInfoTooltip: true
      }));
    if (layoutBuilderMode === DimensionalViewType.THREE_D)
      switchedTo2DModeRef.current = true;
    if (layoutBuilderMode === DimensionalViewType.TWO_D) {
      dispatch(builderActions.updateRoomLayoutThumbnailCaptureStatus({ captureThumbnailStatus: 'start' }));
      gtmClickTracking({
        event: "Preview",
        pageName: "Builder 2D Edit Mode",
      });
    }
    else {
      dispatch(builderActions.updateLayoutBuilderMode({ layoutBuilderMode: targetMode }));
      if (layoutBuilderMode === DimensionalViewType.THREE_D) {
        gtmClickTracking({
          event: "Edit",
          pageName: "Builder Preview Mode",
        });
        gtmPageOnloadTracking({
          event: "page_viewed_2D_edit_mode",
          pageName: "Builder 2D Edit Mode",
        });
      }
      else {
        gtmClickTracking({
          event: "Save_2D_Edit_Mode",
          pageName: "Builder 2D Edit Mode",
        });
        gtmPageOnloadTracking({
          event: "builder_page_viewed",
          pageName: "Builder Preview Mode",
        });
      }

    }
  }

  const saveRoomModelAndNavigateToQuestionnairePage = async () => {
    // debugger
    setEnableSaveButton(false);
    setSaveButtonTitle("Saving...")

    dispatch(builderActions.updatCurrentTab({
      currentTab: ''
    }));
    setValue(undefined);

    console.log("Room Layout Save");
    
    await dispatch(saveRoomLayout({}) as any);

    if (isIOSWebKit) {
      // Send message to iPhone
      if ((window as any)?.webkit?.messageHandlers?.callbackHandler) {
        (window as any).webkit.messageHandlers.callbackHandler.postMessage(`Save`);
      }
    }

    const designId = urlParams.designId;
    const designImage = decodeURIComponent(urlParams.designImage);
    if (designId && designImage) {

    console.log("Update Project Metadata");
      await dispatch(builderActions.updateProjectMetdata({
        metadata: {
          designId: designId,
          designImage: decodeURIComponent(urlParams.designImage)
        }
      }));
    }
    if (urlParams.saveProject == "true") {

      console.log("Project Save Called");
      const resultAction = await dispatch(saveProject({ currentRole }) as any);
      // Check if the result is fulfilled (successful)
      if (saveProject.fulfilled.match(resultAction)) {
        const projectId = resultAction.payload;
        console.log("Save Project -> Project ID:", projectId);

          projectIdFromSave = projectId;
          setEnableSaveButton(true);
          setSaveButtonTitle("Save");
      } else {
        console.error("Failed to save project:", resultAction.payload);
      }
    }

    if (layoutBuilderMode === DimensionalViewType.THREE_D) {
      gtmClickTracking({
        event: "Save",
        pageName: "Builder Preview Mode",
      });
    }
    else {
      gtmClickTracking({
        event: "Save",
        pageName: "Builder 2D Edit Mode",
      });
    }
    await saveHandler();
  }


  useEffect(() => {
    if (captureThumbnailStatus === 'end') {
      dispatch(builderActions.updateLayoutBuilderMode({ layoutBuilderMode: DimensionalViewType.THREE_D }));
      dispatch(builderActions.updateRoomLayoutThumbnailCaptureStatus({ captureThumbnailStatus: '' }));
      setEnableSaveButton(true);
    }
  }, [captureThumbnailStatus]);

  useEffect(() => {
    if (twoDLayoutSaveStatus === TRIGGER_ROOM_LAYOUT_THUMBNAILS_SAVE && layoutBuilderMode === DimensionalViewType.THREE_D && thumbnailImage != null && thumbnailImage !== '') {
      dispatch(saveRoomLayoutThumbnail({ image: thumbnailImage }) as any);
    }
  }, [twoDLayoutSaveStatus, layoutBuilderMode]);

  const productType = designId == null && productId != null ? urlParams.productType || 'SingleProduct' : undefined;
  const includeRecommendations = designId == null && productId != null && isShowcaseFlow;
  const editPreviewLabel = switchedTo2DModeRef.current ? 'Edit' : 'Edit';
  const [showLookingGoodTip, setShowLookingGoodTip] = useState(true);
  const isThreeDLayout = (urlParams.createRoom === undefined || (isCustomRoomFlow && layoutBuilderMode === DimensionalViewType.THREE_D && !isShowcaseFlow && !urlParams.createRoom));

  const mappedTabs: any = tabsMap(productType ? productType : '', productId, includeRecommendations, applyFloorTexturein3DView, applyFloorTexturein2DView, updateWallColor, applyColorTo2DWall, isThreeDLayout ? '3d': '');

  let availableTabKeys = Object.keys(mappedTabs).filter(tabName => isFeatureAvailable(tabName));
  if (isCustomRoomFlow && layoutBuilderMode === DimensionalViewType.TWO_D) {
    availableTabKeys = ['doorsTab', 'windowsTab', 'wallColorTab', 'floorColorTab']
  }
  if (urlParams.createRoom === 'true' && layoutBuilderMode === DimensionalViewType.THREE_D) {
    availableTabKeys = []
  }

  if (designId == null && productId != null && availableTabKeys.includes('designTab')) {
    const designsTabIndex = availableTabKeys.indexOf('designTab');
    availableTabKeys.splice(designsTabIndex, 1, 'furnitureTab');
  }
  const handleChange = (event: SyntheticEvent, newValue: number) => {
    setValue(newValue);
    dispatch(builderActions.updatCurrentTab({
      currentTab: availableTabKeys[newValue]
    }));
  };
  const handleDragStop = (event: any, { x, y }: { x: number, y: number }) => {
    setPanelPosition({ x, y });
  }

  const handleClosePopup = () => {
    setPopupOpen(false);
  };

  const handleKeepEditing = () => {
    // console.log("Keep Editing clicked");
    setPopupOpen(false);

    if (isIOSWebKit) {
      // Send message to iPhone
      if ((window as any)?.webkit?.messageHandlers?.callbackHandler) {
        (window as any)?.webkit?.messageHandlers.callbackHandler.postMessage(`Keep Editing`);
      }
    }
  };

  const handleStartDesigning = () => {

    if (isIOSWebKit) {

      // Send message to iPhone
      if ((window as any)?.webkit?.messageHandlers?.callbackHandler) {
        (window as any)?.webkit?.messageHandlers.callbackHandler.postMessage(`${project.roomModel.thumbnail}`);
      }

    } else {
      if (urlParams.saveProject == "true") {
        window.location.href = `/projects?projectId=${project.projectId}`
      } else {
        //window.location.href= `/builder?projectId=${project.projectId}`
        window.location.href = `/builder?roomId=${project.roomModel.roomId}`
      }
    }
  };

  useEffect(() => {
    const currentTabIndex = availableTabKeys.indexOf(currentTab);
    if (currentTabIndex !== -1 && currentTabIndex !== value) {
      setValue(currentTabIndex);
    } else if (urlParams.createRoom) {
      setValue(undefined);
    }

  }, [currentTab]);


  const saveHandler = async () => {
    await setPopupOpen(true);
  }

  const getTabComponents = () => {
    const comps: JSX.Element[] = [];
    availableTabKeys.map((tabName, index) => {
      comps.push(
        <Tab
          label={
            <Box component="span"
              sx={{
                color: 'black',
                fontWeight: 'bold',
                '&.Mui-selected *': {
                  color: 'primary.main'
                }
              }}>
              {mappedTabs[tabName].label}</Box>}
          icon={mappedTabs[tabName].icon}
          sx={{
            margin: '5px 0',
            padding: 0,
            height: 80,
            backgroundColor: '#fff',
            borderRadius: '8px',
            '&:hover': {
              backgroundColor: 'rgba(0, 205, 205, 0.10)'
            },
            '&.Mui-selected *': {
              color: 'primary.main'
            }
          }}
          {...getTabId(index)}
        />
      );
    });
    return comps;
  }

  const getTrayComponents = (value: number) => {
    const comps: JSX.Element[] = [];
    const tabTitleArr: any =
    {
      doorsTab: "Select a door",
      windowsTab: "Select a window",
      wallColorTab: "Select a wall color",
      floorColorTab: "Select a floor"
    }

    availableTabKeys.map((tabName, index) => {
      const trayComp = mappedTabs[tabName].tray;
      comps.push(
        <>
          <TabPanel value={value} index={index}>{trayComp}</TabPanel>
        </>
      );
    });
    return comps;
  }
  const handleCloseAllInfoSection = () => {
    setIsInfoSectionVisible(false);  // Close all sections
  };

  function InfoButton({ yOffset = 0 }) {
    return <button
      style={{
        position: 'fixed',
        left: `20px`,
        top: urlParams.createRoom ? `100px` : `-40px`,
        width: '24px', // Match the width and height of the tooltip
        height: '24px',
        borderRadius: '50%', // Make button circular
        backgroundColor: 'white', // White background
        color: 'black',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center', // Center text inside the button
        fontSize: '12px', // Font size for button text
        fontFamily: 'Nunito', // Optional: Apply your preferred font
        zIndex: 999
      }}
      onClick={() => {
        console.log('showLookingGoodTip ----> ', showLookingGoodTip);
        console.log('isCustomRoomFlow ----> ', isCustomRoomFlow);
        console.log('layoutBuilderMode ----> ', layoutBuilderMode);
        console.log('openThreeDFurniturePanel ----> ', openThreeDFurniturePanel);
        setShowLookingGoodTip(true);
        setIsInfoSectionVisible(!isInfoSectionVisible);
        gtmClickTracking({
          event: "Open_Info_Preview_Mode",
          pageName: "Builder Preview Mode",
        });
      }}
    >
      <img
        src="icons/Info.svg"    // Source of your SVG image
        alt="Button Icon"  // Accessibility for screen readers
        style={{
          width: '24px',   // Set width of the icon
          height: '24px',  // Set height of the icon
        }}
      />
    </button>
  }

  const renderLookingGoodTip = (
    width: number,
    showLookingGoodTip: boolean,
    isCustomRoomFlow: boolean,
    layoutBuilderMode: string,
    isShowcaseFlow: boolean,
    urlParams: any,
    setShowLookingGoodTip: React.Dispatch<React.SetStateAction<boolean>>,
    gtmClickTracking: Function
  ) => {
    // Check conditions inside the function
    if (
      !isCustomRoomFlow ||
      layoutBuilderMode !== DimensionalViewType.THREE_D ||
      isShowcaseFlow ||
      !urlParams.createRoom ||
      !showLookingGoodTip
    ) {
      return null;  // If conditions aren't met, return null
    }

    return (
      <Box component='div'sx={{
        position: 'absolute',
        zIndex: 99999999,
        width: width < 768 ? '90%' : '25%',
        left: width < 768 ? '5%' : '1%',
        bottom: width < 768 ? '80px' : undefined,
        top: width > 768 ? '90px' : undefined,
        opacity: 1.0,
        display: 'block'
      }}>
        <Box component='div' sx={{
          backgroundColor: 'white',
          padding: '20px 40px',
          position: 'relative',
          borderRadius: '30px',
          boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <Typography fontSize={18} color="#52575C" fontFamily="Nunito" fontWeight={600} align="center">
            Tap on Edit to get started
          </Typography>
          <Typography fontSize={12} color="black" fontFamily="Nunito" align="center" sx={{ marginTop: '16px' }}>
            Add, change, or remove doors, windows when you&apos;re in <b>Edit</b> mode.
            <br />
            When it looks good, tap <b>Save</b>.
          </Typography>
          <Box
          component='div'
            sx={{
              position: 'absolute',
              width: '16px',
              height: '16px',
              top: '15px',
              right: '20px',
              cursor: 'pointer'
            }}
            onClick={() => {
              setShowLookingGoodTip(false);
              gtmClickTracking({
                event: "Close_Info_Preview_mode",
                pageName: "Builder Preview Mode"
              });
            }}
          >
            <CloseIcon fontSize="large" color="action" />
          </Box>
        </Box>
      </Box>
    );
  };

  const renderMobileView = () => {
    const isTwoDLayout = layoutBuilderMode === DimensionalViewType.TWO_D;
    const isThreeDLayout = layoutBuilderMode === DimensionalViewType.THREE_D && urlParams.createRoom && !isShowcaseFlow;

    return (
      <div className="mobile-tabs">
        {/* Render for 2D layout */}
        {isTwoDLayout && !showEditPanel && (
          <Box sx={{ alignContent: 'center', width: '100%' }} component='div'>
            <Stack direction="row" sx={{ height: '30px', width: '100%', justifyContent: 'space-between' }}>
              
              <Button
                variant="contained"
                color="primary"
                onClick={updateBuilderLayoutMode}
                size="small"
                className="mobile-save-button"
                sx={{ height: '30px' }}
              >
                Preview
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={saveRoomModelAndNavigateToQuestionnairePage}
                size="small"
                className="mobile-preview-button"
                disabled={!enableSaveButton}
              >
                {saveButtonTitle}
              </Button>
            </Stack>
          </Box>
        )}

        {/* Render for 3D layout */}
        {isThreeDLayout && (
          <div className="mobile-tabs" style={{ justifyContent: 'center' }}>
            <Stack direction="row" sx={{ height: '30px', width: '100%' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={updateBuilderLayoutMode}
                size="small"
                className="mobile-save-button2"
              >
                {editPreviewLabel}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={saveRoomModelAndNavigateToQuestionnairePage}
                size="small"
                className="mobile-preview-button2"
                disabled={!enableSaveButton}
              >
                {saveButtonTitle}
              </Button>
            </Stack>
          </div>
        )}

        {/* Render for 2D layout with value */}
        {isTwoDLayout && value !== undefined && (
          <Box component='div' className="builder_tab_container" sx={{ height: 'auto', width: 'auto', borderBottom: 'solid 1px #eee', padding: '5px', borderRadius: '20px', zIndex: '99999' }}>
            <Grid className="builder-tab-close" item sx={{ textAlign: 'right', marginBottom: '5px' }}>
              <HighlightOffIcon
                fontSize="large"
                sx={{ color: 'black', fontSize: '16px', cursor: 'pointer' }}
                onClick={() => {
                  dispatch(builderActions.updatCurrentTab({ currentTab: '' }));
                  setValue(undefined);
                }}
              />
            </Grid>
            {getTrayComponents(value)}
          </Box>
        )}

        {/* Popup notification */}
        {isPopupOpen && (
          <NotificationPopup
            open={isPopupOpen}
            onClose={handleClosePopup}
            message="Your room has been saved."
            button1="Keep Editing"
            button2="Start Designing"
            onKeepEditing={handleKeepEditing}
            onStartDesigning={handleStartDesigning}
          />
        )}
      </div>
    );
  };
   //new left tray states
   const [isMenuClosedDesignDetail, setIsMenuClosedDesignDetail] = useState<boolean>(true);
   const [selectedDesignDetailsId, setSelectedDesignDetailsId] = useState<string>('')
   const [items, setItems] = useState([])
   const [isMoreItemsClosed, setIsMoreItemsClosed] = useState<boolean>(true)
   const [activeAccordion, setActiveAccordion] = useState<any>({ parentAccordion: '', childAccordion: '' })
   const [isClosedProductDetails, setIsClosedProductDetails] = useState<boolean>(true)
   const [selectedProductDetailsId, setSelectedProductDetailsId] = useState<string>('')
   const handleCloseProductDetails = (isCloseMenu: boolean) => {
     setIsClosedProductDetails(isCloseMenu)
     setIsMenuClosedDesignDetail(true)
     setIsMoreItemsClosed(true)
     if (isCloseMenu) {
       setSelectedProductDetailsId('')
     }
   };
   const handleSelectedProductDetailId = (itemId: string) => {
     setSelectedProductDetailsId(itemId)
   }
   const handleMenuCloseDesignDetail = (isCloseMenu: boolean) => {
     setIsMenuClosedDesignDetail(isCloseMenu);
     setIsMoreItemsClosed(true)
     if (isCloseMenu) {
       setSelectedDesignDetailsId('')
     }
   };
   const handleSelectedDesignDetailId = (itemId: string) => {
     setSelectedDesignDetailsId(itemId)
   }
   const handleMoreItemsClosed = (isCloseMenu: boolean) => {
     setIsMoreItemsClosed(isCloseMenu)
   }
   const handleActiveAccordion = (activeAccordion: any) => {
     setActiveAccordion((prevState: any) => ({ ...prevState, ...activeAccordion }))
   }
   const handleItems = (items: any) => {
     setItems(items)
   }
  const renderDesktopView = () => {
    const isTwoDLayout = value !== undefined && urlParams.createRoom && layoutBuilderMode === DimensionalViewType.TWO_D;
    const isThreeDLayout = (urlParams.createRoom === undefined || (isCustomRoomFlow && layoutBuilderMode === DimensionalViewType.THREE_D && !isShowcaseFlow && !urlParams.createRoom));
    return (
      <>
        <div className="builder-panel-desktop">
          {/* Render for 2D layout */}
          {isTwoDLayout && (
            <Box component='div' className="builder_tab_container" sx={{ height: 'auto', width: 'auto', borderBottom: 'solid 1px #eee', padding: '5px', borderRadius: '20px', zIndex: '99999' }}>
              <Grid className="builder-tab-close" item sx={{ textAlign: 'right', marginBottom: '5px' }}>
                <HighlightOffIcon
                  fontSize="large"
                  sx={{ color: 'black', fontSize: '16px', cursor: 'pointer' }}
                  onClick={() => {
                    dispatch(builderActions.updatCurrentTab({ currentTab: '' }));
                    setValue(undefined);
                  }}
                />
              </Grid>
              {getTrayComponents(value)}
            </Box>
          )}

          {/* Render for 3D layout or undefined createRoom */}
          {isThreeDLayout && (

            <Box component='div' sx={{ position: 'absolute', left: 0, top: 0, display: 'flex', zIndex: 1299 }}>
              <InfoButton />
              {urlParams.createRoom === undefined && isInfoSectionVisible && (
                <>
                  <InfoSection
                    title="Welcome to your canvas!"
                    content="Here you can arrange your room however you'd like and generate a high-fidelity preview to see how it will turn out."
                    positionX="49vw"
                    positionY="39vh"
                    showCloseIcon
                    onClose={handleCloseAllInfoSection}
                    arrowPosClassName="no_arrow"
                  />
                  
                  <InfoSection
                    content="Control the view of your room here."
                    positionX="calc(100vw - 150px)"
                    positionY="calc(100vh - 150px)"
                    showCloseIcon={false}
                    width="200px"
                    onClose={handleCloseAllInfoSection}
                    arrowPosClassName="down"
                  />
                </>
              )}
              <Box component='div' sx={{ position: 'absolute', top: '20px' }}>

                {value !== undefined && (
                  <>
                  <Box component='div' sx={{ height: 'auto', width: 'auto', backgroundColor: 'white', marginLeft: '18px', padding: '10px', borderRadius: '20px', boxShadow: '3px 4px 8px 0px rgba(0,0,0,0.25)', position: 'relative', zIndex: '2' }}>
                      <GetTrayComponentsForThreeDBuilder
                        isInteriorDesigner={isInteriorDesigner}
                        isShowcaseFlow={isShowcaseFlow}
                        isDesignShowcase={isDesignShowcase}
                        productId={productId}
                        productType={productType}
                        includeRecommendations={includeRecommendations}
                        setIsInfoSectionVisible={setIsInfoSectionVisible}
                        isInfoSectionVisible={isInfoSectionVisible}
                        handleMenuCloseDesignDetail={handleMenuCloseDesignDetail}
                        handleSelectedDesignDetailId={handleSelectedDesignDetailId}
                        handleMoreItemsClosed={handleMoreItemsClosed}
                        handleActiveAccordion={handleActiveAccordion}
                        handleItems={handleItems}
                        handleCloseProductDetails={handleCloseProductDetails}
                        handleSelectedProductDetailId={handleSelectedProductDetailId}
                      />
                    {urlParams.createRoom === undefined && isInfoSectionVisible && (
                      <InfoSection
                        content="Click on the furniture here and drag it into your room."
                        positionX="19vw"
                        positionY="50vh"
                        showCloseIcon={false}
                        width="200px"
                        onClose={handleCloseAllInfoSection}
                        style={{
                          position: 'absolute', 
                          top: '50%',
                          right: '-220px',
                          transform: 'translateY(-50%)',
                          width: '200px',
                          padding: '10px',
                          left: 'auto',
                        }}
                        arrowPosClassName="left"
                      />
                    )}
                  </Box>    
                                  
                    {!isMenuClosedDesignDetail && <Box component="div" className="menu_expanded_section" id='designDetailsMainContainer'>
                      <DesignDetail
                        handleMenuCloseDesignDetail={handleMenuCloseDesignDetail}
                        selectedDesignDetailsId={selectedDesignDetailsId}
                        activeAccordion={activeAccordion}
                        handleMoreItemsClosed={handleMoreItemsClosed}
                        handleActiveAccordion={handleActiveAccordion}
                        handleCloseProductDetails={handleCloseProductDetails}
                        handleSelectedProductDetailId={handleSelectedProductDetailId}
                      />
                    </Box>}
                    {!isMoreItemsClosed && <Box component="div" className="menu_expanded_section" id='moreItemsMainContainer'>
                      <MoreItems
                        handleMoreItemsClosed={handleMoreItemsClosed}
                        activeAccordion={activeAccordion}
                        items={items}
                        handleSelectedDesignDetailId={handleSelectedDesignDetailId}
                        handleMenuCloseDesignDetail={handleMenuCloseDesignDetail}
                        handleActiveAccordion={handleActiveAccordion}
                        handleCloseProductDetails={handleCloseProductDetails}
                        handleSelectedProductDetailId={handleSelectedProductDetailId}
                      />
                    </Box>}
                    {!isClosedProductDetails && <Box component="div" className="menu_expanded_section" id='productDetailsMainContainer'>
                      <ProductDetails
                        handleCloseProductDetails={handleCloseProductDetails}
                        handleSelectedProductDetailId={handleSelectedProductDetailId}
                        selectedProductDetailsId={selectedProductDetailsId}
                        activeAccordion={activeAccordion}
                        handleActiveAccordion={handleActiveAccordion}
                        selectedDesignDetailsId={selectedDesignDetailsId}
                        handleMenuCloseDesignDetail={handleMenuCloseDesignDetail}
                        handleMoreItemsClosed={handleMoreItemsClosed}
                      />
                    </Box>}
                  </>   
                )}
              </Box>
            </Box>

          )}
        </div>
      </>
    );
  };

  const isMobileView: boolean = width < 768;

  return (
    <>
      {isMobileView ? renderMobileView() : renderDesktopView()}

      {renderLookingGoodTip(
        width,
        showLookingGoodTip,
        isCustomRoomFlow,
        layoutBuilderMode,
        isShowcaseFlow,
        urlParams,
        setShowLookingGoodTip,
        gtmClickTracking
      )}

    </>

  );
}
export default ClientComponent(ControlPanel);